const appendCount = (lines, label, value) => {
    if (value === undefined || value === null) return;
    lines.push(`${label} : ${value}`);
};

export const buildTripsPoliciesStandardResultMessage = (response, labels) => {
    if (!response || typeof response !== 'object') {
        return labels.emptyResult;
    }

    const lines = [];
    if (response.dry_run === true) {
        lines.push(labels.dryRunBanner);
        lines.push('');
    }

    if (response.code) {
        lines.push(`${labels.code} : ${response.code}`);
    }
    if (response.message) {
        lines.push(response.message);
        lines.push('');
    }

    const summary = response.summary || {};
    appendCount(lines, labels.totalRows, summary.total_rows ?? summary.totalRows);
    appendCount(lines, labels.rowsProcessed, summary.rows_processed ?? summary.rowsProcessed);
    appendCount(
        lines,
        labels.rowsSkipped,
        summary.skipped_rows_count ?? summary.rows_skipped ?? summary.rowsSkipped
    );
    appendCount(
        lines,
        labels.rowsFailed,
        summary.rejected_company_mismatch_count ?? summary.rows_failed ?? summary.rowsFailed
    );
    appendCount(
        lines,
        labels.partnerImportErrors,
        summary.partner_error_count ?? summary.partnerErrorCount
    );
    appendCount(
        lines,
        labels.partnerErrorsCount,
        getPartnerErrors(response).length || summary.partner_errors_count
    );
    appendCount(
        lines,
        labels.rejectedActivityNotFound,
        summary.rejected_activity_not_found_count ?? summary.rejectedActivityNotFoundCount
    );
    appendCount(
        lines,
        labels.policiesCreated,
        summary.imported_policies_count ?? summary.policies_created ?? summary.policiesCreated
    );
    appendCount(
        lines,
        labels.documentsGenerated,
        summary.documents_generated_count ?? summary.documents_generated ?? summary.documentsGenerated
    );
    appendCount(lines, labels.successCount, response.success_count ?? summary.success_count);
    appendCount(lines, labels.errorCount, response.error_count ?? summary.error_count);

    const subscription = getSubscriptionEmails(response);
    if (subscription) {
        lines.push('');
        lines.push(labels.subscriptionEmailsTitle || 'subscription_emails');
        appendCount(lines, labels.subscriptionEmailsCreated, subscription.created);
        appendCount(lines, labels.subscriptionEmailsSent, subscription.emails_sent);
        if (subscription.copy_email) {
            lines.push(`${labels.subscriptionEmailsCopyEmail || 'copy_email'} : ${subscription.copy_email}`);
        }
        const skippedEmails = Array.isArray(subscription.skipped) ? subscription.skipped : [];
        if (skippedEmails.length > 0) {
            lines.push(`${labels.subscriptionEmailsSkipped || 'skipped'} (${skippedEmails.length}) :`);
            skippedEmails.forEach((entry, index) => {
                const policyCode = entry.policy_code || entry.policyCode || 'N/A';
                const reason = formatSubscriptionEmailReason(entry.reason || entry.message);
                lines.push(`${index + 1}. ${policyCode} — ${reason}`);
            });
        }
        const failedEmails = [
            ...(Array.isArray(subscription.failed) ? subscription.failed : []),
            ...(Array.isArray(subscription.email_failures) ? subscription.email_failures : []),
        ];
        if (failedEmails.length > 0) {
            lines.push(`${labels.subscriptionEmailsFailed || 'failed'} (${failedEmails.length}) :`);
            failedEmails.forEach((entry, index) => {
                const policyCode = entry.policy_code || entry.policyCode || 'N/A';
                const reason = formatSubscriptionEmailReason(entry.reason || entry.error || entry.message);
                lines.push(`${index + 1}. ${policyCode} — ${reason}`);
            });
        }
    }

    if (response.detected_format) {
        lines.push(`${labels.detectedFormat} : ${response.detected_format}`);
    }

    if (Array.isArray(response.all_policies) && response.all_policies.length > 0) {
        lines.push('');
        lines.push(`${labels.createdPolicies} :`);
        response.all_policies.forEach((item, index) => {
            const info = item?.policy_info || item;
            const policyCode = info?.policy_code || info?.policyCode || 'N/A';
            const reservation = info?.reservation_number || info?.number_reservation || 'N/A';
            lines.push(`${index + 1}. ${policyCode} — ${reservation}`);
        });
    } else if (Array.isArray(response.created_policies) && response.created_policies.length > 0) {
        lines.push('');
        lines.push(`${labels.createdPolicies} : ${response.created_policies.join(', ')}`);
    }

    const skippedReservations = getSkippedReservations(response);
    if (skippedReservations.length > 0) {
        lines.push('');
        lines.push(`${labels.skippedReservations} (${skippedReservations.length}) :`);
        skippedReservations.forEach((skipped, index) => {
            const reservationNumber = skipped.reservation_number || skipped.reservationNumber || 'N/A';
            const skipMsg = skipped.message || labels.skippedDefault;
            lines.push(`${index + 1}. ${reservationNumber} — ${skipMsg}`);
        });
    }

    if (Array.isArray(response.rejected_company_mismatch) && response.rejected_company_mismatch.length > 0) {
        lines.push('');
        lines.push(`${labels.rejectedCompanyMismatch} (${response.rejected_company_mismatch.length}) :`);
        response.rejected_company_mismatch.forEach((entry, index) => {
            if (typeof entry === 'string') {
                lines.push(`${index + 1}. ${entry}`);
                return;
            }
            const rowLabel = entry.row != null ? `Ligne ${entry.row}` : `Ligne ${index + 1}`;
            const reservationNumber = entry.reservation_number || entry.reservationNumber || 'N/A';
            const companyName = entry.company_name || entry.companyName || 'N/A';
            const reason = entry.message || entry.reason || labels.unknownError;
            lines.push(`${index + 1}. ${rowLabel} — ${reservationNumber} — ${companyName} — ${reason}`);
        });
    }

    if (Array.isArray(response.rejected_activity_not_found) && response.rejected_activity_not_found.length > 0) {
        lines.push('');
        lines.push(`${labels.rejectedActivityNotFound} (${response.rejected_activity_not_found.length}) :`);
        response.rejected_activity_not_found.forEach((entry, index) => {
            if (typeof entry === 'string') {
                lines.push(`${index + 1}. ${entry}`);
                return;
            }
            const rowLabel = entry.row != null ? `Ligne ${entry.row}` : `Ligne ${index + 1}`;
            const reservationNumber = entry.reservation_number || entry.reservationNumber || 'N/A';
            const activityName = entry.activity_name || entry.activityName || 'N/A';
            const companyName = entry.company_name || entry.companyName || 'N/A';
            const reason = entry.message || entry.reason || labels.unknownError;
            lines.push(`${index + 1}. ${rowLabel} — ${reservationNumber} — ${companyName} — ${activityName} — ${reason}`);
        });
    }

    if (Array.isArray(response.validation_errors) && response.validation_errors.length > 0) {
        lines.push('');
        lines.push(`${labels.validationErrorsTitle || labels.errorsTitle} (${response.validation_errors.length}) :`);
        response.validation_errors.forEach((entry, index) => {
            if (typeof entry === 'string') {
                lines.push(`${index + 1}. ${entry}`);
                return;
            }
            const rowLabel = entry.row != null ? `Ligne ${entry.row}` : `Ligne ${index + 1}`;
            const reservationNumber = entry.reservation_number || entry.reservationNumber || 'N/A';
            const column = entry.column || '';
            const missing = Array.isArray(entry.missing_columns)
                ? entry.missing_columns.join(', ')
                : '';
            const reason = entry.message || entry.reason || labels.unknownError;
            const colPart = column ? ` — ${column}` : '';
            const missingPart = missing ? ` — [${missing}]` : '';
            lines.push(`${index + 1}. ${rowLabel} — ${reservationNumber}${colPart}${missingPart} — ${reason}`);
        });
    }

    if (Array.isArray(response.partner_import_errors) && response.partner_import_errors.length > 0) {
        lines.push('');
        lines.push(`${labels.partnerImportErrorsTitle} (${response.partner_import_errors.length}) :`);
        response.partner_import_errors.forEach((entry, index) => {
            if (typeof entry === 'string') {
                lines.push(`${index + 1}. ${entry}`);
                return;
            }
            const reservationNumber = entry.reservation_number || entry.reservationNumber || 'N/A';
            const activityName = entry.activity_name || entry.activityName || '';
            const errorMsg = entry.error || entry.message || entry.reason || labels.unknownError;
            const startDate = entry.activity_start_date || entry.activityStartDate || '';
            const endDate = entry.activity_end_date || entry.activityEndDate || '';
            const datePart = startDate && endDate ? ` — ${startDate} → ${endDate}` : '';
            const activityPart = activityName ? ` — ${activityName}` : '';
            lines.push(`${index + 1}. ${reservationNumber}${activityPart}${datePart} — ${errorMsg}`);
            const windows = Array.isArray(entry.available_subcoverage_windows)
                ? entry.available_subcoverage_windows
                : [];
            windows.forEach((window) => {
                const start = window.sub_coverage_start_date || window.subCoverageStartDate || '—';
                const end = window.sub_coverage_end_date || window.subCoverageEndDate || '—';
                const subId = window.sub_coverage_id || window.subCoverageId || '';
                const idPart = subId ? ` (${subId})` : '';
                lines.push(`   ↳ ${labels.subcoverageWindow || 'Fenêtre sous-couverture'}${idPart} : ${start} → ${end}`);
            });
        });
    }

    const partnerErrors = getPartnerErrors(response);
    if (partnerErrors.length > 0) {
        lines.push('');
        lines.push(`${labels.partnerErrorsTitle} (${partnerErrors.length}) :`);
        partnerErrors.forEach((entry, index) => {
            if (typeof entry === 'string') {
                lines.push(`${index + 1}. ${entry}`);
                return;
            }
            const partnerId = entry.partner_id || entry.partnerId || 'N/A';
            const partnerType = entry.partner_type || entry.partnerType || '';
            const errorMsg = entry.error || entry.message || entry.reason || labels.unknownError;
            const typePart = partnerType ? ` [${partnerType}]` : '';
            lines.push(`${index + 1}. ${partnerId}${typePart} — ${errorMsg}`);
        });
    }

    if (Array.isArray(response.errors) && response.errors.length > 0) {
        lines.push('');
        lines.push(`${labels.errorsTitle} :`);
        response.errors.forEach((error, index) => {
            const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
            const errorMsg = error.error || error.message || error.reason || labels.unknownError;
            lines.push(`${index + 1}. ${reservationNumber} — ${errorMsg}`);
        });
    }

    if (Array.isArray(response.details) && response.details.length > 0) {
        lines.push('');
        lines.push(`${labels.detailsCount} : ${response.details.length}`);
    }

    if (lines.length === 0) {
        return labels.emptyResult;
    }

    return lines.join('\n');
};

export const buildTripsPoliciesSpecificResultMessage = (response, typeName, labels) => {
    let successMessage = `${labels.importDone} ${typeName}.\n\n`;

    if (response?.success_count !== undefined) {
        successMessage += `✅ ${labels.successCount} : ${response.success_count}\n`;
    }
    if (response?.error_count !== undefined) {
        successMessage += `❌ ${labels.errorCount} : ${response.error_count}\n`;
    }
    if (response?.skipped_reservations && Array.isArray(response.skipped_reservations) && response.skipped_reservations.length > 0) {
        successMessage += `⏭️ ${labels.skippedReservations} : ${response.skipped_reservations.length}\n`;
    }
    if (response?.created_policies && Array.isArray(response.created_policies) && response.created_policies.length > 0) {
        successMessage += `\n📋 ${labels.createdPolicies} : ${response.created_policies.join(', ')}\n`;
    }
    if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0) {
        successMessage += `\n⚠️ ${labels.errorsTitle} :\n`;
        response.errors.forEach((error, index) => {
            const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
            const errorMsg = error.error || error.message || labels.unknownError;
            successMessage += `${index + 1}. ${labels.reservation} ${reservationNumber}: ${errorMsg}\n`;
        });
    }
    if (response?.skipped_reservations && Array.isArray(response.skipped_reservations) && response.skipped_reservations.length > 0) {
        successMessage += `\n⏭️ ${labels.skippedReservations} (${response.skipped_reservations.length}) :\n`;
        response.skipped_reservations.forEach((skipped, index) => {
            const reservationNumber = skipped.reservation_number || skipped.reservationNumber || 'N/A';
            const skipMsg = skipped.message || labels.skippedDefault;
            successMessage += `${index + 1}. ${labels.reservation} ${reservationNumber}: ${skipMsg}\n`;
        });
    }
    if (response?.message) {
        successMessage += `\n${response.message}`;
    }

    return successMessage;
};

export const getStandardImportRejections = (response) => (
    Array.isArray(response?.rejected_company_mismatch) ? response.rejected_company_mismatch : []
);

/** Normalise une entrée skipped (string ou objet { reservation_number, message }). */
export const normalizeSkippedReservation = (entry) => {
    if (entry == null) return null;
    if (typeof entry === 'string') {
        const trimmed = entry.trim();
        return trimmed ? { reservation_number: trimmed, message: null } : null;
    }
    if (typeof entry === 'object') {
        const reservationNumber = entry.reservation_number || entry.reservationNumber || null;
        const message = entry.message || entry.reason || null;
        if (!reservationNumber && !message) return null;
        return { reservation_number: reservationNumber, message };
    }
    return null;
};

/** Réservations ignorées (doublons) depuis skipped_reservation_numbers ou skipped_reservations. */
export const getSkippedReservations = (response) => {
    const fromNumbers = Array.isArray(response?.skipped_reservation_numbers)
        ? response.skipped_reservation_numbers
        : [];
    const fromLegacy = Array.isArray(response?.skipped_reservations)
        ? response.skipped_reservations
        : [];
    const source = fromNumbers.length > 0 ? fromNumbers : fromLegacy;
    return source.map(normalizeSkippedReservation).filter(Boolean);
};

export const hasSkippedReservations = (response) => (
    getSkippedReservations(response).length > 0
);

export const getStandardImportActivityRejections = (response) => (
    Array.isArray(response?.rejected_activity_not_found) ? response.rejected_activity_not_found : []
);

export const hasMissingCompanyNameRejections = (response) => (
    getStandardImportRejections(response).some(
        (entry) => entry?.reason === 'missing_company_name'
            || (entry && !entry.company_name && !entry.companyName),
    )
);

export const hasPartnerNotFoundRejections = (response) => (
    getStandardImportRejections(response).some((entry) => entry?.reason === 'partner_not_found')
);

export const hasStandardScopeRejections = (response) => (
    getStandardImportRejections(response).some((entry) => entry?.reason === 'standard_scope')
);

export const getStandardScopeRejections = (response) => (
    getStandardImportRejections(response).filter((entry) => entry?.reason === 'standard_scope')
);

/** Noms company_name hors périmètre Standard (dédupliqués). */
export const getRejectedStandardScopeCompanyNames = (response) => {
    const names = getStandardScopeRejections(response)
        .map((entry) => entry.company_name || entry.companyName)
        .filter((name) => name != null && String(name).trim() !== '');
    return [...new Set(names)];
};

/** Noms company_name inconnus (dédupliqués) issus des rejets partner_not_found. */
export const getRejectedUnknownCompanyNames = (response) => {
    const names = getStandardImportRejections(response)
        .filter((entry) => entry?.reason === 'partner_not_found')
        .map((entry) => entry.company_name || entry.companyName)
        .filter((name) => name != null && String(name).trim() !== '');
    return [...new Set(names)];
};

/** activity_name inconnus (dédupliqués par partenaire + camping). */
export const getRejectedUnknownActivities = (response) => {
    const seen = new Set();
    return getStandardImportActivityRejections(response).filter((entry) => {
        const companyName = entry.company_name || entry.companyName || '';
        const activityName = entry.activity_name || entry.activityName || '';
        const key = `${companyName}|${activityName}`;
        if (!activityName || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const hasActivityNotFoundRejections = (response) => (
    getStandardImportActivityRejections(response).length > 0
);

export const getStandardValidationErrors = (response) => (
    Array.isArray(response?.validation_errors) ? response.validation_errors : []
);

export const getPartnerImportErrors = (response) => (
    Array.isArray(response?.partner_import_errors) ? response.partner_import_errors : []
);

export const hasPartnerImportErrors = (response) => (
    getPartnerImportErrors(response).length > 0
);

/** Erreurs partenaire (documents S3 manquants, etc.) — champ partner_errors. */
export const getPartnerErrors = (response) => (
    Array.isArray(response?.partner_errors) ? response.partner_errors : []
);

export const hasPartnerErrors = (response) => (
    getPartnerErrors(response).length > 0
);

/** Bloc subscription_emails renvoyé par l'import Standard (confirmation souscription). */
export const getSubscriptionEmails = (response) => (
    response?.subscription_emails && typeof response.subscription_emails === 'object'
        ? response.subscription_emails
        : null
);

export const hasSubscriptionEmails = (response) => Boolean(getSubscriptionEmails(response));

/**
 * Formate une raison d'e-mail (ex. email_list_disabled:01-confirmation-souscription-se_jour).
 * @param {string} reason
 * @param {(code: string) => string} [formatCode]
 */
export const formatSubscriptionEmailReason = (reason, formatCode) => {
    if (!reason || typeof reason !== 'string') return '—';
    const disabledPrefix = 'email_list_disabled:';
    if (reason.startsWith(disabledPrefix)) {
        const code = reason.slice(disabledPrefix.length);
        const label = typeof formatCode === 'function' ? formatCode(code) : code;
        return `email_list_disabled — ${label}`;
    }
    return reason;
};

export const hasValidationErrors = (response) => (
    getStandardValidationErrors(response).length > 0
);

export const hasActivityTypeSameDayValidationErrors = (response) => (
    getStandardValidationErrors(response).some(
        (entry) => entry?.reason === 'activity_type_requires_same_day',
    )
);

export const hasActivityTypeRequiresExcelTimesValidationErrors = (response) => (
    getStandardValidationErrors(response).some(
        (entry) => entry?.reason === 'activity_type_requires_excel_times',
    )
);

export const isTripsPoliciesStandardFailure = (response) => {
    if (!response || typeof response !== 'object') return false;
    if (response.success === false) return true;
    if (hasValidationErrors(response)) return true;
    if (hasPartnerImportErrors(response)) return true;
    if (hasPartnerErrors(response)) return true;
    const summary = response.summary || {};
    const partnerErrorCount = Number(summary.partner_error_count ?? 0);
    if (partnerErrorCount > 0) return true;
    const combined = Number(summary.rejected_company_mismatch_count ?? 0)
        + Number(summary.rejected_activity_not_found_count ?? 0);
    const errorCount = combined > 0
        ? combined
        : (response.error_count ?? summary.error_count ?? summary.rows_failed);
    return errorCount != null && Number(errorCount) > 0;
};

/** Statistiques compactes pour les cartes de résultat (Import Standard). */
export const parseStandardImportStats = (response) => {
    if (!response || typeof response !== 'object') return null;

    const summary = response.summary || {};
    const policiesCreated = summary.imported_policies_count
        ?? summary.policies_created
        ?? summary.policiesCreated
        ?? (Array.isArray(response.all_policies) ? response.all_policies.length : null)
        ?? (Array.isArray(response.created_policies) ? response.created_policies.length : null)
        ?? 0;
    const duplicates = summary.skipped_rows_count
        ?? summary.rows_skipped
        ?? summary.rowsSkipped
        ?? (getSkippedReservations(response).length || null)
        ?? 0;
    const partnerErrors = summary.rejected_company_mismatch_count
        ?? (Array.isArray(response.rejected_company_mismatch) ? response.rejected_company_mismatch.length : null)
        ?? 0;
    const activityErrors = summary.rejected_activity_not_found_count
        ?? (Array.isArray(response.rejected_activity_not_found) ? response.rejected_activity_not_found.length : null)
        ?? 0;
    const validationErrors = summary.validation_errors_count
        ?? (Array.isArray(response.validation_errors) ? response.validation_errors.length : null)
        ?? 0;
    const processorErrors = summary.partner_error_count
        ?? (getPartnerImportErrors(response).length || null)
        ?? 0;
    const partnerDocumentErrors = getPartnerErrors(response).length || 0;
    const documentsGenerated = summary.documents_generated_count
        ?? summary.documents_generated
        ?? summary.documentsGenerated
        ?? summary.documents_created
        ?? summary.documentsCreated
        ?? 0;

    const subscription = getSubscriptionEmails(response);
    const emailsCreated = subscription?.created ?? 0;
    const emailsSent = subscription?.emails_sent
        ?? (Array.isArray(subscription?.sent) ? subscription.sent.length : 0)
        ?? 0;
    const emailsSkipped = Array.isArray(subscription?.skipped) ? subscription.skipped.length : 0;
    const emailsFailed = (
        Array.isArray(subscription?.failed) ? subscription.failed.length : 0
    ) + (
        Array.isArray(subscription?.email_failures) ? subscription.email_failures.length : 0
    );

    return {
        policiesCreated: Number(policiesCreated) || 0,
        duplicates: Number(duplicates) || 0,
        partnerErrors: Number(partnerErrors) || 0,
        activityErrors: Number(activityErrors) || 0,
        validationErrors: Number(validationErrors) || 0,
        processorErrors: Number(processorErrors) || 0,
        partnerDocumentErrors: Number(partnerDocumentErrors) || 0,
        documentsGenerated: Number(documentsGenerated) || 0,
        emailsCreated: Number(emailsCreated) || 0,
        emailsSent: Number(emailsSent) || 0,
        emailsSkipped: Number(emailsSkipped) || 0,
        emailsFailed: Number(emailsFailed) || 0,
    };
};
